/* eslint-disable max-lines */
import { useCallback } from 'react'

import {
  togglePostModalEventDispatch,
  togglePostSidebarFullScreenEventDispatch
} from 'src/utils/customEvents'

import { toastEventDispatch } from '@hotmart/space-lib-events/dist/toast'
import i18n from 'src/i18n'
import { ToastPosition, ToastType } from '@hotmart/space-lib-events/dist/toast/enum'
import { SurvicateUtils, Surveys } from 'src/utils/survicate'
import { ApiError, queryClient } from 'src/config/request'
import { OnboardingEndpoints } from 'src/services/onboarding'
import { HttpCodes } from '@hotmart/space-lib/dist/constants'
import { PostProps, PostType, usePosts } from 'src/hook/usePosts'
import { MEDIAS_PROCESSING_KEY } from 'src/utils/constants'
import { useCreatePostNotification, useTrackEvents } from 'src/components/InputMarkdown/hooks'
import { useUser } from 'src/hook/useUser'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import { useOnboarding } from 'src/pages/Onboarding/hooks/useOnboarding'
import { useSchedulePostForm } from 'src/hook/useSchedulePostForm'
import { MODAL_ACTIONS } from 'src/utils/customEvents/types'
import { useSearchParams } from 'react-router-dom'
import { CommunitiesEndpoints } from 'src/client/constants'
import { usePostSurvey } from 'src/hook/usePostSurvey'
import { CommunityTypeConsume } from 'src/pages/communityHome'
import {
  Action,
  ComponentName,
  ComponentSource,
  Entity,
  FailureDescription,
  System,
  TryResult
} from 'src/utils/datahubDefaultEvent'
import { useSendDatahubDefaultEvents } from 'src/hook/useSendDatahubDefaultEvents'
import { useMentionTrackEvents } from 'src/hook/useMentionTrackEvents'
import { useMentionUtils } from 'src/components/commentInput/hooks'
import { useDeviceInfo } from '@hotmart-org-ca/space-lib/dist/hooks'
import { useNewFeed } from 'src/hook/useNewFeed'
import { ArrivedFrom } from 'src/utils/datahub/enums'
import { usePostCreatePublication } from '../usePostCreatePublication'
import {
  OnCreatePublicationProps,
  SavePublicationTempProps,
  SendEventProps
} from './useCreatePublication.types'

const useCreatePublication = () => {
  const { setPostsProcessing, startCheckPostStatus, postsProcessing } = usePosts()
  const { community } = useCommunityDetails()
  const { avatar, user } = useUser()
  const { onSurveyInit } = usePostSurvey()
  const sendCreatePostNotification = useCreatePostNotification()
  const { dispatchDatahubDefaultEvents } = useSendDatahubDefaultEvents()
  const deviceInfo = useDeviceInfo()
  const { sendCreatePostEvent } = useTrackEvents()
  const { sendPublishContentMentionEvent } = useMentionTrackEvents()
  const { getMentionsCount } = useMentionUtils()
  const { formData, resetForm: resetScheduleForm } = useSchedulePostForm()
  const [searchParams, setSearchParams] = useSearchParams()
  const { enabled: newFeedEnabled, optimizely } = useNewFeed()
  const {
    onboardingSteps: {
      communityAdmin: { communityPostCreate }
    }
  } = useOnboarding()

  const { mutate, isLoading: isLoadingCreatePublication } = usePostCreatePublication()

  /* istanbul ignore next */
  const sendEvent = ({
    communityId,
    communityName,
    communityType,
    result,
    statusCode,
    failureDescription,
    componentName,
    postId
  }: SendEventProps) => {
    dispatchDatahubDefaultEvents({
      system: System.HOTMART_CLUB,
      action: Action.TRY_TO_POST,
      entity: Entity.COMMUNITY,
      event: {
        communityId,
        communityName,
        communityType,
        hasAccessToTheCommunity: true,
        result,
        statusCode,
        failureDescription,
        componentName,
        postId,
        componentSource: ComponentSource.POST_LIST,
        arrivedFrom: newFeedEnabled
          ? ArrivedFrom.COMMUNITY_FEED_V3
          : ArrivedFrom.COMMUNITY_NEW_FEED,
        ...deviceInfo
      }
    })

    optimizely.track(Action.TRY_TO_POST)
  }

  /* istanbul ignore next */
  const removeQueryParams = useCallback(() => {
    const param = searchParams.get('origin')

    if (!param) return

    searchParams.delete('origin')
    setSearchParams(searchParams)
  }, [searchParams, setSearchParams])

  /* istanbul ignore next */
  const savePublicationTemp = ({
    postId,
    publication,
    descriptionOnlyText
  }: SavePublicationTempProps) => {
    const description = descriptionOnlyText

    toastEventDispatch({
      type: ToastType.INFO,
      message: i18n.t('toasts.video_in_processing'),
      position: ToastPosition.BOTTOM_RIGHT
    })

    const objPost: PostProps = {
      id: Number(postId),
      authorName: user?.socialName || user.name,
      authorImageFinalUrl: avatar,
      authorHasImage: false,
      authorMemberType: '',
      title: publication.title as string,
      description: description || '',
      pubDate: new Date().getTime(),
      isAuthor: true,
      postType: PostType.VIDEO,
      authorId: Number(user.id),
      link: '',
      membershipInfo: {
        membershipId: ''
      },
      communityId: community.id,
      isCommentsEnabled: true
    }
    const newTemp = [...postsProcessing, objPost]
    setPostsProcessing(newTemp)
    localStorage.setItem(MEDIAS_PROCESSING_KEY, JSON.stringify(newTemp))
    startCheckPostStatus()
  }

  /* istanbul ignore next */
  const clearCache = (scheduledPosts: boolean) => {
    if (scheduledPosts) {
      queryClient.invalidateQueries({
        queryKey: ['getScheduledPosts']
      })

      return
    }

    queryClient.invalidateQueries({
      queryKey: ['getPosts']
    })

    queryClient.invalidateQueries({
      queryKey: ['getUserPostsByUcode']
    })
  }

  const onCreatePublication = useCallback(
    async ({
      publication,
      descriptionOnlyText,
      handleResetForm,
      isFullScreen = false
    }: OnCreatePublicationProps) => {
      let scheduled

      if (formData.scheduledPubDate) {
        scheduled = {
          scheduledPubDate: formData.pubDate,
          scheduledPubTimeZoneId: formData.scheduledPubTimeZoneId
        }
      }

      const userMentionCount = getMentionsCount(descriptionOnlyText)

      await mutate(
        {
          ...publication,
          collectionId: community.id,
          ...(scheduled?.scheduledPubDate && { ...scheduled })
        },
        {
          /* istanbul ignore next */
          onSuccess(postId) {
            sendPublishContentMentionEvent({
              communityId: Number(community.id),
              communityType: CommunityTypeConsume[community.type],
              communityUserRole: community.permissions?.group,
              componentName: ComponentName.POST_CREATE,
              componentSource: ComponentName.POST_CREATE,
              hasAccessToTheCommunity: true,
              parentEntityId: String(community.id),
              result: TryResult.SUCCESSFUL,
              createdEntityId: String(postId),
              userMentionCount
            })

            if (!scheduled?.scheduledPubDate) {
              toastEventDispatch({
                type: ToastType.SUCCESS,
                message: i18n.t('toasts.publish_success'),
                position: ToastPosition.BOTTOM_RIGHT
              })

              sendCreatePostEvent({
                communityId: community.id,
                communityName: community.name
              })

              sendEvent({
                communityId: Number(community.id),
                communityName: community.name,
                communityType: CommunityTypeConsume[community.type],
                result: TryResult.SUCCESSFUL,
                statusCode: HttpCodes.OK,
                failureDescription: FailureDescription.NOT_FAILURE_RESULT_EVENT,
                componentName: ComponentName.CREATE_POST_MODAL,
                postId: Number(postId)
              })

              queryClient.invalidateQueries({
                queryKey: [CommunitiesEndpoints.getCommunity(community.id)]
              })

              queryClient.invalidateQueries({
                queryKey: [CommunitiesEndpoints.getCommunityBySlug(community.url)]
              })
            } else {
              sendCreatePostNotification('success')
              sendEvent({
                communityId: Number(community.id),
                communityName: community.name,
                communityType: CommunityTypeConsume[community.type],
                result: TryResult.SUCCESSFUL,
                statusCode: HttpCodes.OK,
                failureDescription: FailureDescription.NOT_FAILURE_RESULT_EVENT,
                componentName: ComponentName.CREATE_POST_MODAL_SCHEDULED,
                postId: Number(postId)
              })

              resetScheduleForm()
            }

            clearCache(scheduled)

            if (!communityPostCreate.communityFirstPost) {
              queryClient.invalidateQueries({
                queryKey: [OnboardingEndpoints.getOnboarding(community.id)]
              })
            }

            if (publication.postType === PostType.VIDEO) {
              savePublicationTemp({
                publication,
                descriptionOnlyText,
                postId: postId as string
              })
            }

            SurvicateUtils.showSurvey(Surveys.PUBLICATION_MOTIVATION)

            handleResetForm()
            removeQueryParams()
            onSurveyInit()

            if (!isFullScreen) return togglePostModalEventDispatch({ action: MODAL_ACTIONS.CLOSE })
            return togglePostSidebarFullScreenEventDispatch({ action: MODAL_ACTIONS.CLOSE })
          },
          /* istanbul ignore next */
          onError(error) {
            const err = error as ApiError
            const failureDescription =
              typeof err === 'object' ? JSON.stringify(err)?.slice(0, 255) : err

            sendPublishContentMentionEvent({
              communityId: Number(community.id),
              communityType: CommunityTypeConsume[community.type],
              communityUserRole: community.permissions?.group,
              componentName: ComponentName.POST_CREATE,
              componentSource: ComponentName.POST_CREATE,
              failureDescription,
              hasAccessToTheCommunity: true,
              parentEntityId: String(community.id),
              result: TryResult.FAILURE,
              statusCode: err?.statusCode,
              createdEntityId: 'UNDEFINED',
              userMentionCount
            })

            sendEvent({
              communityId: Number(community.id),
              communityName: community.name,
              communityType: CommunityTypeConsume[community.type],
              result: TryResult.FAILURE,
              statusCode: (error as ApiError).statusCode,
              failureDescription: (error as ApiError)?.message ?? FailureDescription.NO_MESSAGE,
              componentName: scheduled?.scheduledPubDate
                ? ComponentName.CREATE_POST_MODAL_SCHEDULED
                : ComponentName.CREATE_POST_MODAL
            })

            toastEventDispatch({
              type: ToastType.DANGER,
              message: i18n.t('toasts.error_in_publish'),
              position: ToastPosition.BOTTOM_RIGHT
            })

            return sendCreatePostNotification(
              (error as ApiError).statusCode === HttpCodes.FORBIDDEN ? 'date_error' : 'error'
            )
          }
        }
      )
    },
    [
      mutate,
      toastEventDispatch,
      sendEvent,
      sendCreatePostEvent,
      SurvicateUtils,
      togglePostModalEventDispatch,
      togglePostSidebarFullScreenEventDispatch
    ]
  )

  return {
    onCreatePublication,
    isLoadingCreatePublication
  }
}

export default useCreatePublication
